import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Brain, Search, Check, Trash2, ShieldQuestion, ChevronLeft } from "lucide-react";
import { MemoryApi } from "../lib/api";
import { PageHeader } from "../components/PageScaffold";
import { Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";

const TIERS = ["all", "short", "long", "cold"] as const;
const KINDS = ["all", "fact", "event", "preference", "procedure"] as const;
function tierTone(t: string): "accent" | "info" | "neutral" {
  return t === "short" ? "accent" : t === "long" ? "info" : "neutral";
}

// Sort dimensions. "recent" preserves the server order (created_at DESC). The
// others re-rank the loaded set: by last recall, ACT-R activation (strength),
// or raw recall count — the views a maintainer reaches for to find what's
// actually load-bearing vs stale.
type SortKey = "recent" | "recalled" | "strength" | "used";
const SORT_LABEL: Record<SortKey, string> = {
  recent: "Newest", recalled: "Last recalled", strength: "Strongest", used: "Most recalled",
};

export default function Memory() {
  const [agent, setAgent] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState<string>("all");
  const [kind, setKind] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortKey>("recent");
  const [review, setReview] = useState(false);
  const qc = useQueryClient();
  const { data: agents = [], isLoading } = useQuery({ queryKey: ["memory", "agents"], queryFn: MemoryApi.agents, refetchInterval: 15_000 });
  const sel = agent ?? agents[0]?.agent ?? null;
  // Mobile is single-pane: show the agent list until one is explicitly tapped,
  // then the memory view (with a Back bar). md+ shows both side by side.
  const detailOpen = agent !== null;
  const { data: mems = [] } = useQuery({
    queryKey: ["memory", sel, q, tier, review],
    queryFn: () => MemoryApi.list(sel!, { q: q || undefined, tier, review: review ? "1" : undefined }),
    enabled: !!sel,
  });

  // Kind filter + sort are client-side over the loaded set (server already
  // handles q / tier / review and returns newest-first).
  const shown = useMemo(() => {
    const arr = kind === "all" ? mems : mems.filter((m) => (m.kind ?? "fact") === kind);
    if (sortBy === "recent") return arr;
    const ts = (s: string | null) => (s ? new Date(s).getTime() : 0);
    return [...arr].sort((a, b) => {
      switch (sortBy) {
        case "recalled": return ts(b.last_recalled_at) - ts(a.last_recalled_at);
        case "strength": return b.activation - a.activation;
        case "used": return b.recall_count - a.recall_count;
        default: return 0;
      }
    });
  }, [mems, kind, sortBy]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["memory", sel] });
    qc.invalidateQueries({ queryKey: ["memory", "agents"] });
  };
  const forget = useMutation({ mutationFn: (id: number) => MemoryApi.forget(sel!, id), onSuccess: invalidate });
  const confirm = useMutation({ mutationFn: (id: number) => MemoryApi.confirm(sel!, id), onSuccess: invalidate });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader title="Memory" icon={Brain} subtitle={agents.length ? `${agents.length} agents with memory` : "per-agent long-term memory"} />
      <div className="flex-1 min-h-0 panel mx-3 md:mx-5 mb-5 overflow-hidden flex">
        <div className={cn("w-full md:w-52 border-r border-border overflow-y-auto md:flex-shrink-0", detailOpen ? "hidden md:block" : "block")}>
          {agents.map((a) => (
            <button
              key={a.agent}
              onClick={() => setAgent(a.agent)}
              className={cn("w-full text-left px-3 py-2 border-b border-border hover:bg-surface-2 transition-colors", sel === a.agent && "bg-surface-2")}
            >
              <div className="text-sm font-medium text-fg flex items-center gap-1.5">
                {a.agent}
                {!!a.review && <span className="text-[10px] px-1 rounded bg-warning/20 text-warning font-mono" title={`${a.review} memories need review`}>{a.review}</span>}
              </div>
              <div className="text-[10px] text-fg-subtle font-mono">{a.total} mem · {a.short}s {a.long}l {a.cold}c</div>
            </button>
          ))}
          {agents.length === 0 && <div className="p-4 text-xs text-fg-subtle">No agent memory yet — agents create it via the <code className="font-mono">remember</code> tool.</div>}
        </div>
        <div className={cn("flex-1 flex-col min-h-0 min-w-0", detailOpen ? "flex" : "hidden md:flex")}>
          {detailOpen && (
            <button
              type="button"
              onClick={() => setAgent(null)}
              className="md:hidden flex items-center gap-1 px-3 py-2.5 border-b border-border text-sm text-fg-muted hover:text-fg bg-surface flex-shrink-0"
            >
              <ChevronLeft className="size-4" /> {sel}
            </button>
          )}
          <div className="px-3 py-2 border-b border-border space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search memories…" className="w-full pl-8 h-7 text-xs" />
              </div>
              <span className="text-[11px] text-fg-subtle font-mono flex-shrink-0">{shown.length}{shown.length !== mems.length ? `/${mems.length}` : ""}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => setReview((v) => !v)}
                className={cn("h-7 px-2 text-xs rounded inline-flex items-center gap-1 border border-border transition-colors", review ? "bg-warning/20 text-warning" : "text-fg-subtle hover:bg-surface-2")}
                title="Show only low-confidence (inferred) memories that need a human to confirm or correct"
              >
                <ShieldQuestion className="size-3.5" /> review
              </button>
              <select value={tier} onChange={(e) => setTier(e.target.value)} disabled={review} className="h-7 text-xs flex-1 min-w-[5rem] disabled:opacity-40" title="Tier">
                {TIERS.map((t) => <option key={t} value={t}>{t === "all" ? "all tiers" : t}</option>)}
              </select>
              <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-7 text-xs flex-1 min-w-[6rem]" title="Kind">
                {KINDS.map((k) => <option key={k} value={k}>{k === "all" ? "all kinds" : k}</option>)}
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)} className="h-7 text-xs flex-1 min-w-[7.5rem]" title="Sort by">
                {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => <option key={k} value={k}>↕ {SORT_LABEL[k]}</option>)}
              </select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {shown.map((m) => (
              <div key={m.id} className={cn("px-4 py-2.5 group", !m.current && "opacity-60")}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Pill tone={tierTone(m.tier)}>{m.tier}</Pill>
                  {m.kind && m.kind !== "fact" && <Pill tone="neutral">{m.kind}</Pill>}
                  {typeof m.confidence === "number" && m.confidence < 1 && (
                    <Pill tone="warning">{Math.round(m.confidence * 100)}% sure</Pill>
                  )}
                  {m.subject && <span className="text-[11px] font-mono text-fg-subtle">[{m.subject}]</span>}
                  {!m.current && <Pill tone="warning">superseded</Pill>}
                  <span className="ml-auto inline-flex items-center gap-1">
                    {m.current && (typeof m.confidence === "number" && m.confidence < 1) && (
                      <button
                        onClick={() => confirm.mutate(m.id)}
                        disabled={confirm.isPending}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-success/20 text-success"
                        title="Confirm — mark this fact correct (removes it from review)"
                      ><Check className="size-3.5" /></button>
                    )}
                    <button
                      onClick={() => { if (window.confirm(`Forget this memory?\n\n"${m.content.slice(0, 140)}"`)) forget.mutate(m.id); }}
                      disabled={forget.isPending}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-danger/20 text-danger"
                      title="Forget — delete this memory permanently"
                    ><Trash2 className="size-3.5" /></button>
                    <span className="text-[10px] text-fg-subtle font-mono whitespace-nowrap">act {m.activation} · ×{m.recall_count}</span>
                  </span>
                </div>
                <div className={cn("text-sm text-fg", !m.current && "line-through")}>{m.content}</div>
                {m.about && m.about.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.about.map((a) => (
                      <span key={a.id} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-2 text-fg-muted">
                        about: {a.name ?? `#${a.id}`}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-fg-subtle mt-0.5">
                  created {relativeTime(m.created_at)}{m.last_recalled_at ? ` · recalled ${relativeTime(m.last_recalled_at)}` : " · never recalled"}
                  {m.source_kind ? ` · via ${m.source_kind}` : ""}
                </div>
              </div>
            ))}
            {sel && shown.length === 0 && (
              <div className="p-6"><EmptyState icon={<Brain className="size-7" />} title={review ? "Nothing to review" : "No memories match"} /></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
